/**
 * ProductDetails Component
 * Displays details of a soil mix product and allows adding to cart
 * 
 * @module components/ProductDetails
 * @requires react
 * @requires context/CartContext
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import api from '../api';
import './ProductDetails.css';

/**
 * ProductDetails component
 * @returns {JSX.Element} Product details page
 */
const ProductDetails = () => {
  // Get product ID from URL parameters
  const { id } = useParams();
  // State for product data
  const [product, setProduct] = useState(null);
  // State for loading status
  const [loading, setLoading] = useState(true);
  // State for error messages
  const [error, setError] = useState(null);
  // State for quantity
  const [quantity, setQuantity] = useState(1);
  // State for added to cart message
  const [addedToCart, setAddedToCart] = useState(false);
  
  // Get cart functions from cart context
  const { addToCart } = useCart(); 


  /**
   * Fetch product data when component mounts or ID changes
   */
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await api.get(`/api/plants/${id}`);
        setProduct(response.data);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  /**
   * Handle quantity change
   * @param {Event} e - Change event
   */
  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value > 0) {
      setQuantity(value);
    }
  };

/**
 * Clean ingredients by removing percentages and extra formatting
 * @param {string[]} ingredients - Array of ingredient strings
 * @returns {string[]} Clean ingredient names
 */
const cleanIngredients = (ingredients) => {
  return ingredients.map(ingredient => 
    ingredient
      .replace(/^\d+%\s*/, '')           // Remove "40% " from start
      .replace(/,?\s*$/, '')             // Remove trailing commas/spaces
      .trim()                            // Remove extra whitespace
      .toLowerCase()                     // Make lowercase for consistency
      .replace(/^./, char => char.toUpperCase()) // Capitalize first letter
  );
};

  /**
   * Handle add to cart button click
   */
  const handleAddToCart = async () => {
    try {
      await addToCart(product._id, quantity);
      
      // Show success message
      setAddedToCart(true);
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setAddedToCart(false);
      }, 3000);
    } catch (err) {
      console.error('Error adding to cart:', err);
    }
  };

  // Show loading message
  if (loading) {
    return <div className="product-details loading">Loading product details...</div>;
  }

  // Show error message
  if (error) {
    return <div className="product-details error">{error}</div>;
  }

  // Show not found message
  if (!product) {
    return <div className="product-details not-found">Product not found</div>;
  }

  return (
    <div className="product-details">
      <div className="product-image">
      <img 
        src={product.soilMix.image || '/images/default-soil.jpg'}
        alt={`${product?.name || 'Plant'} Soil Mix`}
      />
      </div>
      
      <div className="product-info">
        <h2>{product.soilMix.name}</h2>
        <p className="price">${product.soilMix.price.toFixed(2)}</p>
        <p className="description">{product.soilMix.description}</p>
        
        <div className="specifications">
          <h3>Specifications</h3>
          <ul>
            <li><strong>Ingredients:</strong> {cleanIngredients(product.soilMix?.ingredients || []).join(', ')}</li>
            <li><strong>Benefits:</strong> {product.soilMix?.benefits?.join(', ') || 'Not specified'}</li>
          </ul>
        </div>
        
        <div className="quantity-selector">
          <label htmlFor="quantity">Quantity:</label>
          <input 
            type="number" 
            id="quantity" 
            min="1" 
            value={quantity} 
            onChange={handleQuantityChange}
          />
        </div>
        
        <button 
          className="add-to-cart-btn" 
          onClick={handleAddToCart}
          disabled={addedToCart}
        >
          {addedToCart ? 'Added to Cart!' : 'Add to Cart'}
        </button>
        
        {addedToCart && (
          <div className="success-message">
            Product added to cart successfully!
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetails;